import React from 'react';
import { Box, Avatar, Typography, Button, Divider, Alert } from '@mui/material';
import { ChatBubbleOutline as ChatIcon, DeleteOutline as DeleteIcon, Block as BlockIcon } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { api } from '../../api/chat';
import {
  refreshBlocked,
  refreshContacts,
  refreshConversations,
  setActiveConversation,
  loadMessages,
  setView,
  setRightContent,
  removeConversation,
} from '../../redux/chat.redux';
import { avatarText, anonTag } from '../../utils/format';import { userBackground } from '../../lib/background';
import PanelShell from './panel-shell';

interface Props {
  userId: number;
  onClose: () => void;
  embedded?: boolean;
}

// 个人名片(通讯录视图右侧详情)
export default function ContactCard({ userId, onClose, embedded }: Props) {
  const dispatch = useAppDispatch();
  const contacts = useAppSelector((s) => s.chat.contacts);
  const blocked = useAppSelector((s) => s.chat.blocked);
  const conversations = useAppSelector((s) => s.chat.conversations);
  const user = contacts.find((c) => c.id === userId);
  const isBlocked = blocked.some((b) => b.id === userId);

  if (!user) {
    return (
      <PanelShell title="个人名片" onClose={onClose} embedded={embedded}>
        <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>未找到该联系人</Box>
      </PanelShell>
    );
  }

  const sendMessage = async () => {
    try {
      const conv = await api.openPrivate(userId);
      dispatch(refreshConversations());
      dispatch(setActiveConversation(conv.id));
      dispatch(loadMessages(conv.id));
      dispatch(setView('conversations'));
      dispatch(setRightContent({ type: 'chat' }));
    } catch (err: any) {
      alert(err?.message || '无法发起会话');
    }
  };

  const blockToggle = async () => {
    try {
      if (isBlocked) {
        await api.unblockUser(userId);
      } else {
        await api.blockUser(userId);
      }
      dispatch(refreshBlocked());
      if (!isBlocked) {
        const conv = conversations.find((c) => c.type === 'private' && c.peer_id === userId);
        if (conv) dispatch(removeConversation(conv.id));
      }
    } catch (err: any) {
      alert(err?.message || '操作失败');
    }
  };

  const deleteContact = async () => {
    if (!window.confirm(`确定删除好友 ${user.nickname || user.username} 吗?`)) return;
    try {
      await api.deleteContact(userId);
      dispatch(refreshContacts());
      dispatch(refreshConversations());
      const conv = conversations.find((c) => c.type === 'private' && c.peer_id === userId);
      if (conv) dispatch(removeConversation(conv.id));
      dispatch(setRightContent({ type: 'none' }));
    } catch (err: any) {
      alert(err?.message || '删除失败');
    }
  };

  return (
    <PanelShell title="个人名片" onClose={onClose} embedded={embedded}>
      <Box sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 3,
            borderRadius: 2,
            background: userBackground(user.id),
            mb: 2,
          }}
        >
          <Avatar src={user.avatar || undefined} sx={{ width: 84, height: 84, fontSize: 34, bgcolor: 'primary.main', border: '3px solid #fff', boxShadow: 1 }}>
            {avatarText(user)}
          </Avatar>
          <Typography sx={{ mt: 1.5, fontWeight: 600 }}>{user.nickname || user.username}</Typography>
          <Typography variant="caption" color="text.secondary">
            @{user.username} · {user.online ? '在线' : '离线'} · 匿名 {anonTag(user.id)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button fullWidth variant="contained" startIcon={<ChatIcon />} onClick={sendMessage} sx={{ borderRadius: 2 }}>
            发消息
          </Button>
          <Button fullWidth variant="outlined" color={isBlocked ? 'success' : 'error'} startIcon={<BlockIcon />} onClick={blockToggle} sx={{ borderRadius: 2 }}>
            {isBlocked ? '解除拉黑' : '拉黑'}
          </Button>
        </Box>
        <Button fullWidth variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={deleteContact} sx={{ borderRadius: 2 }}>
          删除好友
        </Button>

        {isBlocked && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            你已拉黑该用户,无法向其发送消息。
          </Alert>
        )}
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          添加为好友后即可实时聊天;删除好友后对方将不在你的通讯录中,聊天记录将被隐藏。
        </Typography>
      </Box>
    </PanelShell>
  );
}
